import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import SaveIcon from '@mui/icons-material/SaveAlt';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {translations} from '../../../../../../i18n/translations';
import {useObserveViewport} from '../../../../../../hooks';
import {ScreenPaths} from '../../../../../../enums';
import {EntityShiftsAllocationsTableRow} from '../../../../../../backend/interfaces/entity-shifts-allocations-table';
import {EntityShiftsRepository} from '../../../../../../repositories/entity-shifts';
import {EntityShiftsAllocationsRepository} from '../../../../../../repositories/entity-shifts-allocations';
import ShiftsAllocationsTable from '../../../../../../components/shifts-allocations/shifts-allocations-table/shifts-allocations-table';
import {DateStepper} from '../../../../../../components/task-table/date-stepper';
import {useFetchAllItems} from '../../../../../../hooks/fetch-all-items';
import {useAuth} from '../../../../../../contexts/auth/context';
import {useExportDataWithErrorHandler} from '../../../../../../hooks/use-export-data-with-error-handler';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {EntityShiftAllocationTableData} from '../../../../../../backend/interfaces/entity-shift-allocation-table-data';
import {prepareShiftsAllocationsTablePDF} from '../../../../../../utils/data/export-data/shifts-allocations/export-data-shifts-allocations-pdf';
import {prepareShiftsAllocationsXLSX} from '../../../../../../utils/data/export-data/shifts-allocations/export-data-shifts-allocations-xlsx';

const ShiftsAllocationsProfessionalCaregiverOfficeScreen = () => {
  const {entityId}: {entityId?: string} = useParams();
  const {isRunningMobile} = useObserveViewport();
  const {t} = useTranslation();
  const {hasWritePermission, state: authState} = useAuth();
  const {previewPdfWithErrorHandling, previewXlsxWithErrorHandling} =
    useExportDataWithErrorHandler();
  const canWriteAllocations = hasWritePermission(RolePermissionType.Team_Shifts_Attributions);
  const today = moment();
  const [tableRows, setTableRows] = useState<EntityShiftsAllocationsTableRow[] | undefined>([]);
  const [startDate, setStartDate] = useState(today.startOf('isoWeek'));
  const endDate = useMemo(() => startDate.clone().add(1, 'week'), [startDate]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const {items: shifts} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      return EntityShiftsRepository.instance.getShifts(entityId!, limit, skip);
    },
  });

  const fetchShiftsAllocations = useCallback(async () => {
    const response =
      await EntityShiftsAllocationsRepository.instance.getTableFormatEntityShiftsAllocations(
        entityId!,
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      );

    setTableRows(response.lines);
    setIsLoading(false);
  }, [entityId, endDate, startDate]);

  useEffect(() => {
    fetchShiftsAllocations();
  }, [fetchShiftsAllocations, startDate]);

  const handleShiftAllocationChanged = async (
    userId: string,
    day: string,
    entityShiftId: string,
    toDelete: boolean
  ) => {
    if (!toDelete) {
      await EntityShiftsAllocationsRepository.instance
        .updateEntityShiftAllocation(
          entityId!,
          userId,
          moment(day).format('YYYY-MM-DD'),
          entityShiftId
        )
        .then((response) => {
          setTableRows((prev) => {
            const newTableRows = [...prev!];
            newTableRows
              .find((row) => row.user.id === userId)
              ?.allocations.push({day: day, entityShiftId: response.entityShiftId});

            return newTableRows;
          });
        });
    } else {
      await EntityShiftsAllocationsRepository.instance
        .removeEntityShiftAllocation(
          entityId!,
          userId,
          moment(day).format('YYYY-MM-DD'),
          entityShiftId
        )
        .then(() => {
          setTableRows((prev) => {
            const newTableRows = [...prev!];
            const rowToUpdate = newTableRows.find((row) => row.user.id === userId);
            if (!rowToUpdate) return newTableRows;

            const newAllocations: EntityShiftAllocationTableData[] = [];
            const actualAllocations = rowToUpdate.allocations.filter(
              (allocation) => allocation.day === day
            );
            rowToUpdate.allocations.forEach((allocation) => {
              if (allocation.day !== day || allocation.entityShiftId !== entityShiftId) {
                newAllocations.push(allocation);
              } else if (allocation.day === day && actualAllocations.length === 1) {
                newAllocations.push({day});
              }
            });

            rowToUpdate.allocations = newAllocations;
            return newTableRows;
          });
        });
    }
  };

  const handleExportPdf = useCallback(async () => {
    if (!tableRows || !shifts || isLoading || isExportingPdf) {
      return;
    }

    await previewPdfWithErrorHandling(async () => {
      const entity = authState.session?.selectedAffiliation?.entity;
      const currentUser = authState.session?.currentUser;
      const exportPdfTitle = t(translations.teamSideMenu.shifts.shiftsAllocations);

      if (!entity) {
        return null;
      }

      setIsExportingPdf(true);

      try {
        const pdf = await prepareShiftsAllocationsTablePDF({
          title: exportPdfTitle,
          startDate,
          endDate: endDate.clone().subtract(1, 'day'),
          user: currentUser,
          entity,
          t,
        });

        return {
          file: pdf.file,
          fileName: pdf.fileName,
        };
      } finally {
        setIsExportingPdf(false);
      }
    });
  }, [
    authState.session?.currentUser,
    authState.session?.selectedAffiliation?.entity,
    endDate,
    isExportingPdf,
    isLoading,
    previewPdfWithErrorHandling,
    shifts,
    startDate,
    t,
    tableRows,
  ]);

  const handleExportXlsx = useCallback(async () => {
    if (!tableRows || !shifts || isLoading) {
      return;
    }

    await previewXlsxWithErrorHandling(async () => {
      const entity = authState.session?.selectedAffiliation?.entity;

      if (!entity) {
        return null;
      }

      const xlsx = prepareShiftsAllocationsXLSX({
        entity,
        rows: tableRows,
        shifts,
        startDate,
        endDate,
        t,
      });

      return {
        file: xlsx.file,
        fileName: xlsx.fileName,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authState.session?.selectedAffiliation?.entity,
    endDate,
    isLoading,
    previewXlsxWithErrorHandling,
    shifts,
    startDate,
    t,
    tableRows,
  ]);

  return (
    <>
      <GenericHeader
        titleCrumbs={
          isRunningMobile
            ? [
                {
                  label: t(
                    translations.office.professional_caregiver.shifts_allocations.generic_header
                      .breadcrumbs.shifts_allocations
                  ),
                  link: '#',
                },
              ]
            : [
                {
                  label: t(
                    translations.office.professional_caregiver.shifts_allocations.generic_header
                      .breadcrumbs.team
                  ),
                  link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`,
                },
                {
                  label: t(
                    translations.office.professional_caregiver.shifts_allocations.generic_header
                      .breadcrumbs.shifts_allocations
                  ),
                  link: '#',
                },
              ]
        }
        isLoading={!shifts || !tableRows}
        leftColumn={<></>}
        rightColumn={
          <DateStepper
            setIsLoading={setIsLoading}
            value={startDate}
            onChange={setStartDate}
            range="week"
          />
        }
        actions={{
          actions: [
            {
              label: t(translations.office.generics.save_pdf),
              icon: {element: SaveIcon},
              callback: handleExportPdf,
            },
            {
              label: t(translations.office.generics.save_xlsx),
              icon: {element: SaveIcon},
              callback: handleExportXlsx,
            },
          ],
        }}
      />
      <ShiftsAllocationsTable
        canWriteAllocations={canWriteAllocations}
        startDate={startDate}
        endDate={endDate}
        shifts={shifts}
        rows={tableRows}
        isLoading={isLoading}
        isExporting={isExportingPdf}
        onShiftAllocationChanged={handleShiftAllocationChanged}
      />
    </>
  );
};

export default ShiftsAllocationsProfessionalCaregiverOfficeScreen;
